/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FC, useEffect, useRef, useState } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { MimaPatientProps } from "../../types/studies";
import { IPatientIdResponse } from "../../types/patient";

export const PatientId: FC<MimaPatientProps> = ({
  patientIds,
  issuerEnabled,
  preventClickToView = false,
  maxWidth = "100%",
  placement = "right",
  widthStyle = undefined,
}) => {
  const [show, setShow] = useState(false);
  const pointRef = useRef(false);
  const [copied, setCopied] = useState(false);
  const [selectedPid, setSelectedPid] = useState("");

  // hide popover on right click so it doesn't overlap the table context menu.
  const hidePopover = () => {
    pointRef.current = false;
    setShow(false);
    setSelectedPid("");
    setCopied(false);
  };

  const pidComponent = (
    <span
      onMouseEnter={() => {
        setShow(true);
      }}
      onMouseLeave={() => {
        setTimeout(() => {
          if (!pointRef.current) setShow(false);
        }, 300);
      }}
      onContextMenu={hidePopover}
      className="pid-no-wrap"
      // inline-block: size to the text so the popover anchors next to it (not at the
      // container's right edge), still respects parent text-align, and maxWidth keeps ellipsis.
      style={{
        maxWidth: maxWidth,
        width: widthStyle,
        ...(widthStyle === undefined && {
          display: "inline-block",
          verticalAlign: "bottom",
        }),
      }}
      onClick={(e) => {
        if (preventClickToView) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      {patientIds?.map((patientId) => patientId.pid).join(", ")}
    </span>
  );

  const renderPatientText = (patient: IPatientIdResponse) => {
    return issuerEnabled ? (
      <span>
        {patient?.pid} - {patient?.issuer?.namespaceId}
      </span>
    ) : (
      <span>{patient.pid}</span>
    );
  };

  useEffect(() => {
    return () => {
      pointRef.current = false;
    };
  }, []);

  return (
    <>
      <OverlayTrigger
        trigger={["hover", "focus"]}
        placement={placement}
        show={show}
        rootClose
        // fixed: position against the viewport so the first placement is correct
        // (absolute jumps from top-left when rendered inside modals/scroll containers).
        popperConfig={{ strategy: "fixed" }}
        overlay={
          <Popover
            onMouseEnter={() => {
              setShow(true);
              pointRef.current = true;
            }}
            onMouseLeave={() => {
              setTimeout(() => {
                setShow(false);
                pointRef.current = false;
                setSelectedPid("");
                setCopied(false);
              }, 100);
            }}
            onContextMenu={hidePopover}
            id={Date.now().toString()}
            className="popover-wrapper"
          >
            <div
              className="m-2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <ul className="mb-0 list-unstyled">
                {patientIds?.map((patientId) => (
                  <li className="text-left" key={patientId.pid}>
                    {renderPatientText(patientId)}
                    <i
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigator.clipboard.writeText(`${patientId.pid}`);
                        setCopied(true);
                        setSelectedPid(patientId.pid);
                      }}
                      className={`${
                        copied && selectedPid === patientId.pid
                          ? "fa fa-check text-success"
                          : "icon-copy"
                      } ml-2 cursor-pointer`}
                    ></i>
                  </li>
                ))}
              </ul>
            </div>
          </Popover>
        }
      >
        {pidComponent}
      </OverlayTrigger>
    </>
  );
};
